import isNil from 'lodash/isNil';
import { applySnapshot, Instance, types, effect, reaction$, action, cast } from 'mst-effect';
import { withRunInAction } from 'utils/withRunInAction';
import { CANVAS_INTERACTION_STATE, DISPLAY_ACTION_STATE } from 'utils/konva/types';
import { UiRegionItem } from 'utils/cfUiItems/base';
import { map } from 'rxjs';
import { either } from 'utils/fp';
import { pipe } from 'fp-ts/lib/function';
import { Concept } from 'api/grpc/resources';
import { toJS } from 'mobx';
import { CanvasRegionAnnotation, ZoomType } from 'modules/Canvas/helpers/types';
import { patchAnnotationsTE } from 'api/annotations/patchAnnotations';
import { getAnnotationsFromRegions } from 'utils/annotations/getAnnotationsFromRegions';
import { makeAnnotationEditors } from 'utils/annotations/makeAnnotationEditors';

const displayProperties = types.model({ value: types.number });

export enum DrawingType {
  BoundingBox = 'bbox',
  Polygon = 'polygon',
  Mask = 'mask',
  Point = 'point',
  PointPositive = 'pointPositive',
  PointNegative = 'pointNegative',
}

export const isDrawingPositiveNegativePoint = (drawingType?: DrawingType): boolean =>
  drawingType !== undefined && [DrawingType.PointPositive, DrawingType.PointNegative].includes(drawingType);

export const isDrawingPointPositive = (drawingType?: DrawingType): boolean => drawingType === DrawingType.PointPositive;

export const displayStateModal = types.model({
  [DISPLAY_ACTION_STATE.BRIGHTNESS]: displayProperties,
  [DISPLAY_ACTION_STATE.SATURATION]: displayProperties,
  [DISPLAY_ACTION_STATE.CONTRAST]: displayProperties,
  [DISPLAY_ACTION_STATE.INVERT_COLORS]: displayProperties,
  [DISPLAY_ACTION_STATE.ROTATE]: displayProperties,
  [DISPLAY_ACTION_STATE.SAVED_LOCALLY]: displayProperties,
});

const AnnotationTagModel = types.model({
  annotation: types.maybe(types.frozen<CF.API.Annotations.Annotation>()),
  concept: types.frozen<CF.API.Concept>(),
});

// Create TypeScript types from the models
export type DisplayPropertiesType = Instance<typeof displayProperties>;
export type DisplayStateModalType = Instance<typeof displayStateModal>;
export const DefaultDisplayState: DisplayStateModalType = {
  BRIGHTNESS: { value: 1 },
  SATURATION: { value: 1 },
  CONTRAST: { value: 1 },
  INVERT_COLORS: { value: 0 },
  ROTATE: { value: 0 },
  SAVED_LOCALLY: { value: 0 },
};

export type CanvasStore = Instance<typeof CanvasModel>;
export type AnnotationTag = Instance<typeof AnnotationTagModel>;

const createAnnotationsModel = types.model({}).extend(() => {
  let createAnnotations: TCreateAnnotations = (_a) => Promise.resolve();
  let requestParams = { inputId: '', userId: '', appId: '' };
  let overwriteAnnotations: (a?: CF.API.Annotations.Annotation[]) => unknown = (_a) => {};
  let createAnnotationsOptimistically: (...concepts: Concept[]) => void;
  return {
    views: {
      // note this one is NOT a getter (NOT a computed value)
      // if this were a getter this value would get stale upon observation
      getAnnoState() {
        return {
          createAnnotations,
          overwriteAnnotations,
          requestParams,
          createAnnotationsOptimistically,
        };
      },
    },
    actions: {
      setCreateAnnotations(fn: unknown) {
        createAnnotations = fn as TCreateAnnotations;
      },
      setOverwriteAnnotations(fn: TCreateAnnotations) {
        overwriteAnnotations = fn;
      },
      setUnobservableData({ userId, appId, inputId }: { userId: string; appId: string; inputId: string }) {
        requestParams = { userId, appId, inputId };
      },
      setCreateAnnotationsOptimistically(fn: typeof createAnnotationsOptimistically) {
        createAnnotationsOptimistically = fn;
      },
    },
  };
});

export const CanvasModel = types
  .compose(
    withRunInAction(),
    createAnnotationsModel,
    types
      .model({
        currentConcept: types.frozen<{
          name: string;
          id: string;
          color: string;
          app_id: string;
        } | null>(),
        // TODO: keep only "DRAWING" in this state instead of DRAWING_POLYGON etc cause drawing type can be stored in self.creationType instead
        interactionState: types.optional(
          types.enumeration<CANVAS_INTERACTION_STATE>(Object.values(CANVAS_INTERACTION_STATE)),
          CANVAS_INTERACTION_STATE.EDITING,
        ),
        selectedShape: types.frozen<string | null>(null),
        preventDrawing: types.optional(types.boolean, false),
        regions: types.optional(types.array(types.frozen<UiRegionItem>()), []),
        annotationTags: types.optional(types.array(AnnotationTagModel), []),
        creationType: types.maybe(types.enumeration(Object.values(DrawingType))),
        hoveredItem: types.maybe(types.string),
        editingItem: types.maybe(types.string),
        zoomType: types.maybeNull(types.enumeration('zoom', Object.values(ZoomType))),
        zoomScale: types.optional(types.number, 1),
        hasDrawn: types.maybe(types.boolean),
        cancelDrawing: types.maybe(types.boolean),
        displayActionState: types.maybe(types.enumeration<DISPLAY_ACTION_STATE>(Object.values(DISPLAY_ACTION_STATE))),
        displayStateValues: types.optional(displayStateModal, DefaultDisplayState), // Brightness %, Saturation %, Invert Colors
        hidePopoverBoxes: types.optional(types.boolean, false),
        hiddenConcepts: types.optional(types.array(types.string), []),
        currentRegionPane: types.maybe(types.string),
        offsetX: types.optional(types.number, 0), // X offset for panning
        offsetY: types.optional(types.number, 0), // Y offset for panning
        mediaStatus: types.optional(types.string, 'loading'),
      })
      // for each edited region item, store the final updated state so as to send minimal API calls
      .volatile(() => ({
        editMap: {} as Record<
          string, // this string refers to `item.key`
          {
            item: CanvasRegionAnnotation;
            shapeUpdate: { type: 'bbox'; bounding_box: CF.API.BoundingBox } | { type: 'polygon'; points: CF.API.Polygon['points'] };
          }
        >,
      }))
      .actions((self) => ({
        setHoveredItem: (id: string | undefined) => {
          self.hoveredItem = id;
        },
        setEditingItem: (id: string | undefined) => {
          self.editingItem = id;
        },
        setHasDrawn: (hasDrawn?: boolean) => {
          self.hasDrawn = hasDrawn;
        },
        setCancelDrawing: (cancelDrawing: boolean) => {
          self.cancelDrawing = cancelDrawing;
        },
        setCreationType: (creationType?: DrawingType) => {
          self.creationType = creationType;
        },
        setMediaStatus: (status: string) => {
          self.mediaStatus = status;
        },
      }))
      // temporary views due to interaction state and current item type not being stored separately
      .views((self) => ({
        get isDrawing() {
          return self.interactionState.startsWith('DRAWING_');
        },
      }))
      .named('CanvasModel'),
  )
  .views((self) => ({
    get isDrawing() {
      return self.interactionState.startsWith('DRAWING_');
    },
    get isDragging() {
      return self.interactionState.startsWith('DRAGGING');
    },
    get drawingStateFromCreationType() {
      switch (self.creationType) {
        case DrawingType.BoundingBox:
          return CANVAS_INTERACTION_STATE.DRAWING_BOX;
        case DrawingType.Polygon:
          return CANVAS_INTERACTION_STATE.DRAWING_POLYGON;
        case DrawingType.Mask:
          return CANVAS_INTERACTION_STATE.DRAWING_MASK;
        case DrawingType.Point:
          return CANVAS_INTERACTION_STATE.DRAWING_POINT;
        case DrawingType.PointPositive:
          return CANVAS_INTERACTION_STATE.DRAWING_POINT_POSITIVE;
        case DrawingType.PointNegative:
          return CANVAS_INTERACTION_STATE.DRAWING_POINT_NEGATIVE;

        default:
          return null;
      }
    },
  }))
  .actions((self) => ({
    setHideConcepts(conceptIds: string[]) {
      self.hiddenConcepts = cast(conceptIds);
    },
    setSelectedConcept({
      drawingType,
      payload,
    }: {
      drawingType?: CANVAS_INTERACTION_STATE | undefined;
      payload: Partial<typeof self.currentConcept> | undefined;
    }) {
      self.currentConcept = payload as typeof self.currentConcept;
      const type = drawingType || self.drawingStateFromCreationType;
      if (type) {
        self.interactionState = type;
      }
    },
    setInteractionState(payload: CANVAS_INTERACTION_STATE) {
      self.interactionState = payload;
      if (payload === CANVAS_INTERACTION_STATE.EDITING) {
        self.creationType = undefined;
      }
      if (payload === CANVAS_INTERACTION_STATE.DRAGGING) {
        self.creationType = undefined;
      }
    },
    setDisplayState(payload: DISPLAY_ACTION_STATE | undefined) {
      self.displayActionState = payload;
    },
    setHidePopoverBoxes(hidePopoverBoxes: boolean) {
      self.hidePopoverBoxes = hidePopoverBoxes;
    },
    setCurrentRegionPane(currentRegionPane?: string) {
      self.currentRegionPane = currentRegionPane;
    },
    setDisplayStateValues(key: DISPLAY_ACTION_STATE, value: Partial<DisplayPropertiesType>) {
      const currentValue = self.displayStateValues[key];
      self.displayStateValues = { ...self.displayStateValues, [key]: { ...currentValue, ...value } };
    },
    setOverrideDisplayStateValues(payload: DisplayStateModalType) {
      self.displayStateValues = payload;
    },
    setResetDisplayStateValues() {
      this.setOverrideDisplayStateValues(DefaultDisplayState);
    },
    setRotationOnCanvas() {
      const currentState = self.displayStateValues[DISPLAY_ACTION_STATE.ROTATE];

      let value = 0;
      switch (currentState.value) {
        case 0:
          value = 90;
          break;
        case 90:
          value = 180;
          break;
        case 180:
          value = 270;
          break;
        case 270:
          value = 0;
          break;
        default:
          value = 0;
      }

      self.displayStateValues = { ...self.displayStateValues, [DISPLAY_ACTION_STATE.ROTATE]: { value } };
    },
    setSelectedShape(payload: typeof self.selectedShape) {
      self.selectedShape = payload;
    },
    setPreventDrawingStatus(payload: boolean) {
      self.preventDrawing = payload;
    },
    clearRegions() {
      self.regions = cast([]);
    },
    goIdle() {
      self.interactionState = CANVAS_INTERACTION_STATE.EDITING;
    },
    setAnnotationTags(annotationTags: AnnotationTag[]): void {
      self.annotationTags = cast(annotationTags);
    },
    removeAnnotationTag(annotationTag: AnnotationTag): void {
      self.annotationTags = cast(self.annotationTags.filter(({ annotation }) => annotation?.id !== annotationTag.annotation?.id));
    },
  }))
  .actions((self) => ({
    checkIfNeedToResetELT() {
      if (!self.displayStateValues.SAVED_LOCALLY.value) {
        self.setResetDisplayStateValues();
      }
    },
  }))
  .actions((self) => ({
    zoomIn() {
      self.zoomType = ZoomType.Zoom_In;
    },
    zoomOut() {
      self.zoomType = ZoomType.Zoom_Out;
    },
    setZoomScale(scale: number) {
      self.zoomScale = scale;
    },
    setOffset(offset: { x: number; y: number }) {
      self.offsetX = offset.x;
      self.offsetY = offset.y;
    },
  }))
  /**
   * USER DRAWING JOURNEY
   */
  .actions((self) => ({
    addAnnotationTag(annotationTag: AnnotationTag): void {
      self.annotationTags.push(cast(annotationTag));
    },
    /**
     * STEP 1 of clicking on drawing action icons
     */
    drawing_initiateSelection(creationType: typeof self.creationType) {
      self.creationType = creationType;
      self.interactionState = self.drawingStateFromCreationType || CANVAS_INTERACTION_STATE.EDITING;
    },
    /**
     * STEP 2 of clicking on drawing action icons
     */
    drawing_startWithConcept(concept: typeof self.currentConcept) {
      self.setSelectedConcept({ payload: concept });
      // set correct interaction state
      if (self.drawingStateFromCreationType) self.interactionState = self.drawingStateFromCreationType;
    },
    /**
     * STEP 3 of clicking on drawing action icons (if user decides to cancel)
     */
    async drawing_complete() {
      self.creationType = undefined;

      const {
        createAnnotations,
        requestParams: { inputId, userId },
      } = self.getAnnoState();
      if (!createAnnotations || !inputId) {
        console.warn('CanvasStore not connected to annotations store.');
        return;
      }

      if (!self.regions.length) {
        return;
      }

      const hasSelectedConcepts = !!self.annotationTags.length;

      if (hasSelectedConcepts) {
        const annotations = getAnnotationsFromRegions(self.regions, inputId, userId);

        if (annotations.length) {
          await createAnnotations(annotations);
        }
      }

      self.setSelectedConcept({ payload: null });
      applySnapshot(self.regions, []);
    },
    cleanup() {
      self.setCancelDrawing(false);
      self.setSelectedShape(null);
      self.setEditingItem(undefined);
      self.setAnnotationTags([]);
      self.setCreationType(undefined);
      self.setCurrentRegionPane(undefined);
      self.goIdle();
    },
  }))
  .actions((self) => ({
    editing_initiate(uiItem?: CanvasRegionAnnotation) {
      self.interactionState = CANVAS_INTERACTION_STATE.EDITING;

      if (uiItem?.key && !isNil(uiItem.annotation)) {
        self.setSelectedShape(uiItem.key);

        if (uiItem.concepts?.length) {
          self.setAnnotationTags([
            {
              annotation: toJS(uiItem.annotation),
              concept: uiItem.concepts[0],
            },
          ]);
        }
      }
    },

    async editing_done() {
      const updatedAnnos: CF.API.Annotations.Annotation[] = Object.values(self.editMap)
        .filter((editMetadata) => !!editMetadata.item.annotation)
        .map((editMetadata) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const editors = makeAnnotationEditors(editMetadata.item.annotation!);
          if (editMetadata.shapeUpdate.type === 'bbox') {
            const bbox = editMetadata.shapeUpdate.bounding_box;
            return editors.editBbox({ bbox });
          } else if (editMetadata.shapeUpdate.type === 'polygon') {
            const points = editMetadata.shapeUpdate.points;
            return editors.editPolygon({ points });
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return editMetadata.item.annotation!;
        });

      self.editMap = {};
      self.setSelectedShape(null);
      const { requestParams, overwriteAnnotations } = self.getAnnoState();

      if (!updatedAnnos.length) return;

      // TODO move to gRPC
      const response = await patchAnnotationsTE(requestParams.userId as string, requestParams.appId, {
        action: 'overwrite',
        annotations: updatedAnnos.map((annotation) => ({
          input_id: annotation.input_id,
          id: annotation.id,
          data: annotation.data,
        })),
      })();

      pipe(
        response,
        either.fold(
          () => {
            // overwriteAnnotations();
          },
          (data) => {
            overwriteAnnotations(data.annotations);
          },
        ),
      );
    },

    changeTools(event: React.KeyboardEvent<Element>) {
      const key = event.key.toLowerCase();
      switch (key) {
        case 'v':
          this.editing_initiate();
          break;
        case 'b':
          self.drawing_initiateSelection(DrawingType.BoundingBox);
          break;
        case 'p':
          self.drawing_initiateSelection(DrawingType.Polygon);
          break;
        case 'd':
          self.drawing_initiateSelection(DrawingType.Point);
          break;
        case 'h':
          self.setInteractionState(CANVAS_INTERACTION_STATE.DRAGGING);
          break;
        default:
          break;
      }
    },
  }))
  .actions((self) => {
    effect<string>(self, () =>
      reaction$(() => self.interactionState, { fireImmediately: false }).pipe(
        map(({ current, prev }) =>
          action(() => {
            if (prev.startsWith('DRAWING_')) {
              if (current.startsWith('DRAWING_') && self.hasDrawn) {
                self.drawing_complete();
                self.setAnnotationTags([]);
                self.clearRegions();
              } else if (current === CANVAS_INTERACTION_STATE.EDITING) {
                self.setSelectedShape(null);

                if (self.hasDrawn) {
                  self.setHasDrawn(false);
                  self.drawing_complete();
                }
              }
            } else if (prev === CANVAS_INTERACTION_STATE.EDITING) {
              if (current === CANVAS_INTERACTION_STATE.EDITING) {
                self.editing_done();
              } else if (current.startsWith('DRAWING_')) {
                self.setAnnotationTags([]);
              }
            }
          }),
        ),
      ),
    );
    return {
      beforeDestroy: () => {
        self.editing_done();
      },
    };
  })
  .named('CanvasStore');

export function createCanvasStore(...args: Parameters<typeof CanvasModel.create>): ReturnType<typeof CanvasModel.create> {
  const modelInstance = CanvasModel.create(...args);
  return modelInstance;
}

type TCreateAnnotations = (batchOfAnnotations?: DeepPartial<CF.API.Annotations.Annotation>[]) => Promise<void> | void;
